import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Copy, ArrowUpDown, Trash2 } from "lucide-react";
import { Toast } from "../../components/Toast";

type Mode = "encode" | "decode";

const encodeToBase64 = (text: string): string => {
  try {
    // Use TextEncoder to properly handle UTF-8 characters
    const encoder = new TextEncoder();
    const bytes = encoder.encode(text);
    const binaryString = Array.from(bytes)
      .map((byte) => String.fromCharCode(byte))
      .join("");
    return btoa(binaryString);
  } catch {
    return "";
  }
};

const decodeFromBase64 = (base64: string): { result: string; error: boolean } => {
  try {
    // Remove any whitespace that might have been added
    const cleanBase64 = base64.replace(/\s/g, "");
    if (!cleanBase64) return { result: "", error: false };
    
    const binaryString = atob(cleanBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const decoder = new TextDecoder();
    return { result: decoder.decode(bytes), error: false };
  } catch {
    return { result: "", error: true };
  }
};

export function Base64Encoder() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("encode");
  const [input, setInput] = useState("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const { output, hasError } = useMemo(() => {
    if (!input.trim()) {
      return { output: "", hasError: false };
    }

    if (mode === "encode") {
      return { output: encodeToBase64(input), hasError: false };
    } else {
      const { result, error } = decodeFromBase64(input);
      return { output: result, hasError: error };
    }
  }, [input, mode]);

  const handleCopy = useCallback(async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setToastMessage(t("base64Encoder.copied"));
    } catch {
      setToastMessage(t("base64Encoder.copyFailed"));
    }
  }, [output, t]);

  const handleSwap = useCallback(() => {
    if (!output || hasError) return;
    setInput(output);
    setMode(mode === "encode" ? "decode" : "encode");
  }, [output, hasError, mode]);

  const handleClear = useCallback(() => {
    setInput("");
  }, []);

  const placeholder =
    mode === "encode"
      ? t("base64Encoder.inputPlaceholderEncode")
      : t("base64Encoder.inputPlaceholderDecode");

  return (
    <div className="base64-encoder">
      <div className="b64-toggle-section">
        <button
          type="button"
          className={`b64-toggle-btn ${mode === "encode" ? "active" : ""}`}
          onClick={() => setMode("encode")}
        >
          {t("base64Encoder.encode")}
        </button>
        <button
          type="button"
          className={`b64-toggle-btn ${mode === "decode" ? "active" : ""}`}
          onClick={() => setMode("decode")}
        >
          {t("base64Encoder.decode")}
        </button>
      </div>

      <div className="b64-field">
        <label className="b64-label">{t("base64Encoder.inputLabel")}</label>
        <textarea
          className="b64-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
        />
      </div>

      <div className="b64-field">
        <label className="b64-label">{t("base64Encoder.outputLabel")}</label>
        <textarea
          className={`b64-textarea b64-output ${hasError ? "b64-error" : ""}`}
          value={hasError ? t("base64Encoder.invalidBase64") : output}
          readOnly
          placeholder={t("base64Encoder.outputPlaceholder")}
        />
      </div>

      <div className="b64-actions">
        <button
          type="button"
          className="b64-action-btn"
          onClick={handleCopy}
          disabled={!output || hasError}
        >
          <Copy size={16} />
          {t("base64Encoder.copy")}
        </button>
        <button
          type="button"
          className="b64-action-btn"
          onClick={handleSwap}
          disabled={!output || hasError}
        >
          <ArrowUpDown size={16} />
          {t("base64Encoder.swap")}
        </button>
        <button
          type="button"
          className="b64-action-btn"
          onClick={handleClear}
          disabled={!input}
        >
          <Trash2 size={16} />
          {t("base64Encoder.clear")}
        </button>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
